/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { AutoRefreshTokenService, provideKeycloak, UserActivityService, withAutoRefreshToken } from 'keycloak-angular';
import { AppConfigService } from './core/services/app-config/app-config.service';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApiInterceptor } from './auth/interceptors/api.interceptor';
import { JwtHelperService, JWT_OPTIONS } from '@auth0/angular-jwt';
import { AppConfigSettings } from './core/models/app-config';
import { ApplicationConfig } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';

export const provideKeycloakAngular = (config: any) => provideKeycloak({
	config: {
		url: config.keycloakUrl,
		realm: config.keycloakRealm,
		clientId: config.keycloakClientId
	},
	initOptions: {
		onLoad: 'check-sso',
		silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
		redirectUri: window.location.origin
	},
	features: [
		withAutoRefreshToken({
			onInactivityTimeout: 'logout',
			sessionTimeout: 60000
		})
	],
	providers: [AutoRefreshTokenService, UserActivityService]
});

export const appConfig = (configSettings: AppConfigSettings): ApplicationConfig => {
	return {
		providers: [
			{
				provide: AppConfigService,
				useFactory: () => {
					const configSvc = new AppConfigService();
					configSvc.setSettings(configSettings);
					return configSvc;
				}
			},
			...(configSettings.demoMode ? [] : [provideKeycloakAngular(configSettings)]),
			provideRouter(routes),
			provideHttpClient(withInterceptors([ApiInterceptor])),
			{ provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
			JwtHelperService
		]
	};
};